import React, { useCallback, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from "use-query-params";

import { Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { Selector, SelectorValue } from "../../components/selectors";
import { ColumnTitle, TableNotification } from "../../components/table";
import { reportsFetchRequest } from "../../store/reports/actions";
import { Report, ReportType } from "../../store/reports/types";
import { RootState } from "../../store/root_reducer";
import { getSortOrder } from "../../utils/table";

import styles from "./styles.module.scss";

export const ReportsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["reports_page", "common"]);

  // -- Hooks Start --
  const [pagination, setPagination] = useQueryParams({
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, 10),
  });
  const [reportType, setReportType] = useQueryParam("reportType", withDefault(StringParam, "default"));
  const [ordering, setOrdering] = useQueryParam("ordering", withDefault(StringParam, "-createdAt"));
  // -- Hooks End --

  // -- Stores Start --
  const reportData = useSelector((state: RootState) => state.Reports);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  // -- Stores End --

  // -- Hook Effects Start --
  useEffect(() => {
    setPagination((state) => state, "replaceIn");
    setReportType((state) => state, "replaceIn");
    setOrdering((state) => state, "replaceIn");
  }, [setOrdering, setPagination, setReportType]);

  const fetch = useCallback(() => {
    dispatch(
      reportsFetchRequest({
        ordering,
        reportType,
        gateId: selectedGate.id,
        page: pagination.page,
        pageSize: pagination.pageSize,
      })
    );
  }, [dispatch, pagination, ordering, selectedGate, reportType]);

  useEffect(() => {
    fetch();
  }, [fetch]);
  // -- Hook Effects End --

  const columns = [
    {
      title: <ColumnTitle title={t("table.columns.date.title")} helpText={t("table.columns.date.help")} />,
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder: getSortOrder("createdAt", ordering),
    },
    {
      title: <ColumnTitle title={t("table.columns.filename.title")} />,
      dataIndex: "filename",
      key: "filename",
    },
    {
      title: <ColumnTitle title={t("table.columns.reportType.title")} />,
      dataIndex: "reportType",
      key: "reportType",
      render: (text: string, item: Report) => t(`reportTypes.${item.reportType}`),
      align: "center" as const,
    },
    {
      title: <ColumnTitle title={t("table.columns.actions.title")} />,
      key: "actions",
      align: "center" as const,
      render: (record: Report) => (
        <a href={record.url} target="_blank" rel="noreferrer">
          {t("common:download")}
        </a>
      ),
    },
  ];

  const defaultOption: SelectorValue = { value: "default", title: t("common:any") };
  const reportTypeSelectorOptions = Object.values(ReportType).map((type, idx) => ({
    value: type,
    title: t(`reportTypes.${type}`),
  }));

  return (
    <Page title={t("title")} description={t("description")}>
      <PageHeader onReload={fetch} isLoading={reportData.isLoading}>
        <Selector
          value={reportType}
          onSelect={setReportType}
          className={styles.reportType}
          loading={reportData.isLoading}
          title={t("reportTypeSelector.title")}
          placeholder={t("reportTypeSelector.placeholder")}
          options={[defaultOption, ...reportTypeSelectorOptions]}
        />
      </PageHeader>
      <TableNotification />
      <Table
        bordered
        size="small"
        columns={columns}
        loading={reportData.isLoading}
        dataSource={reportData.reports}
        footer={() => `${t("common:table.total")}: ${reportData.count}`}
        pagination={{
          total: reportData.count,
          current: pagination.page,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "100"],
          onChange: (page, pageSize) => {
            if (page && pageSize) {
              setPagination((currentPagination) => ({
                ...currentPagination,
                pageSize,
                page: pageSize !== currentPagination.pageSize ? 1 : page,
              }));
            }
          },
        }}
        showSorterTooltip={false}
        sortDirections={["ascend", "descend", "ascend"]}
        onChange={(_, filters, sorter: SorterResult<Report> | SorterResult<Report>[], extra) => {
          if (!Array.isArray(sorter)) {
            setOrdering(`${sorter.order === "descend" ? "-" : ""}${sorter.column?.dataIndex}`);
          }
        }}
      />
    </Page>
  );
};
